import { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";

export default function NewAdmissionModal({ onCreated, prefill = null, trigger }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [wards, setWards] = useState([]);
  const [form, setForm] = useState({
    patient_id: "", doctor_id: "", ward_id: "",
    reason_for_admission: "", chief_complaint: "",
    past_medical_history: "", allergies: "", current_medications: "",
    temperature: "", blood_pressure: "", heart_rate: "", respiratory_rate: "",
  });

  useEffect(() => {
    if (!open) return;
    Promise.all([
      // Item 1 — today's OPD patients by default, with already_admitted flag.
      api.get("/ipd/available-patients-for-admission?filter_type=today")
         .then((r) => setPatients(r.data)).catch(() => {}),
      api.get("/doctors").then((r) => setDoctors(r.data)).catch(() => {}),
      api.get("/ipd/wards").then((r) => setWards(r.data)).catch(() => {}),
    ]);
    if (prefill) setForm((f) => ({ ...f, ...prefill }));
  }, [open, prefill]);

  // Item 1 — server-side search when receptionist types.
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      api.get("/ipd/available-patients-for-admission?filter_type=today")
         .then((r) => setPatients(r.data)).catch(() => {});
      return;
    }
    const t = setTimeout(() => {
      api.get(`/ipd/available-patients-for-admission?filter_type=search&search_query=${encodeURIComponent(q)}`)
         .then((r) => setPatients(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query, open]);

  const submit = async () => {
    if (!form.patient_id || !form.doctor_id || !form.ward_id ||
        !form.reason_for_admission || form.reason_for_admission.length < 2) {
      toast.error("Patient, doctor, ward and reason are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        patient_id: form.patient_id, doctor_id: form.doctor_id, ward_id: form.ward_id,
        reason_for_admission: form.reason_for_admission,
        chief_complaint: form.chief_complaint,
        past_medical_history: form.past_medical_history,
        allergies: form.allergies,
        current_medications: form.current_medications
          ? form.current_medications.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 50)
          : [],
        vitals_on_admission: {
          temperature: form.temperature ? Number(form.temperature) : null,
          blood_pressure: form.blood_pressure || null,
          heart_rate: form.heart_rate ? Number(form.heart_rate) : null,
          respiratory_rate: form.respiratory_rate ? Number(form.respiratory_rate) : null,
        },
      };
      await api.post("/ipd/admissions", payload);
      toast.success("Patient admitted");
      setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to admit");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="ipd-new-admission-btn">
            <Plus size={14} className="mr-1" /> New Admission
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ipd-new-admission-modal">
        <DialogHeader><DialogTitle>New IPD Admission</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Patient *</Label>
            <Input placeholder="Search today's OPD by name / phone / id"
                   value={query} onChange={(e) => setQuery(e.target.value)}
                   className="mb-2" data-testid="ipd-adm-patient-search" />
            <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
              <SelectTrigger data-testid="ipd-adm-patient">
                <SelectValue placeholder={patients.length ? `Choose from ${patients.length} patient(s)` : "No patients — try search"} />
              </SelectTrigger>
              <SelectContent>
                {patients.map((p) => (
                  <SelectItem key={p.opd_row_id || p.id} value={p.id}
                              disabled={p.already_admitted}>
                    {p.patient_name || "Unnamed"}
                    {p.patient_mobile ? ` · ${p.patient_mobile}` : ""}
                    {p.doctor_name ? ` · ${p.doctor_name}` : ""}
                    {p.date ? ` · ${p.date}` : ""}
                    {p.already_admitted ? "  (already admitted)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-400 mt-1">
              Showing {patients.length} match{patients.length === 1 ? "" : "es"}
              {query.length >= 2 ? " · searching all history" : " · today's OPD"}
            </p>
          </div>
          <div>
            <Label>Doctor *</Label>
            <Select value={form.doctor_id} onValueChange={(v) => setForm({ ...form, doctor_id: v })}>
              <SelectTrigger data-testid="ipd-adm-doctor"><SelectValue placeholder="Select doctor" /></SelectTrigger>
              <SelectContent>
                {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Ward *</Label>
            <Select value={form.ward_id} onValueChange={(v) => setForm({ ...form, ward_id: v })}>
              <SelectTrigger data-testid="ipd-adm-ward"><SelectValue placeholder="Select ward" /></SelectTrigger>
              <SelectContent>
                {wards.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.ward_name} · {w.available_beds}/{w.total_beds} free
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Reason for admission *</Label>
            <Textarea value={form.reason_for_admission} maxLength={500}
                      onChange={(e) => setForm({ ...form, reason_for_admission: e.target.value })}
                      data-testid="ipd-adm-reason" />
          </div>
          <div className="md:col-span-2">
            <Label>Chief complaint</Label>
            <Textarea value={form.chief_complaint} maxLength={1000}
                      onChange={(e) => setForm({ ...form, chief_complaint: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>Past medical history</Label>
            <Textarea value={form.past_medical_history} maxLength={2000}
                      onChange={(e) => setForm({ ...form, past_medical_history: e.target.value })} />
          </div>
          <div>
            <Label>Allergies</Label>
            <Input value={form.allergies} maxLength={500}
                   onChange={(e) => setForm({ ...form, allergies: e.target.value })} />
          </div>
          <div>
            <Label>Current medications (comma-separated)</Label>
            <Input value={form.current_medications}
                   onChange={(e) => setForm({ ...form, current_medications: e.target.value })} />
          </div>
          <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-2 pt-2">
            <div>
              <Label>Temp (°C)</Label>
              <Input type="number" step="0.1" value={form.temperature}
                     onChange={(e) => setForm({ ...form, temperature: e.target.value })} />
            </div>
            <div>
              <Label>BP (e.g. 120/80)</Label>
              <Input value={form.blood_pressure}
                     onChange={(e) => setForm({ ...form, blood_pressure: e.target.value })} />
            </div>
            <div>
              <Label>Heart Rate</Label>
              <Input type="number" value={form.heart_rate}
                     onChange={(e) => setForm({ ...form, heart_rate: e.target.value })} />
            </div>
            <div>
              <Label>Respiratory Rate</Label>
              <Input type="number" value={form.respiratory_rate}
                     onChange={(e) => setForm({ ...form, respiratory_rate: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="ipd-adm-submit">
            {saving ? "Admitting…" : "Admit Patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
